import React from 'react';

import { Box, Button, Grid } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { LAST_4_SSN_MASK, SSN_MASK } from '@clearverify/constants';

import { EmployeeSearchInputKindEnum } from 'src/api';
import { DateField, MaskedTextField, TextField } from 'src/components/finalForm';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';

import { IdentitySearchFormFields } from './constants';
import type { IdentitySearchFormValues } from './types';
import { identityCriteriaToValues, identityValuesToCriteria } from './utils';
import { identitySearchFormValidationSchema } from './validation';

type IdentitySearchFormProps = {
  onSubmit: (criteria: EmployeeSearchCriteria) => void;
  loading?: boolean;
  /** Search restored from the URL; prefills the form when it's an identity search. */
  initialCriteria?: EmployeeSearchCriteria | null;
};

export const IdentitySearchForm: React.FC<IdentitySearchFormProps> = ({
  onSubmit,
  loading,
  initialCriteria,
}) => {
  const handleSubmit = React.useCallback(
    (values: IdentitySearchFormValues) => onSubmit(identityValuesToCriteria(values)),
    [onSubmit],
  );

  // Held in state so the reference stays stable — `Form` reinitializes on a new one.
  const [initialValues] = React.useState<Partial<IdentitySearchFormValues>>(() =>
    initialCriteria?.kind === EmployeeSearchInputKindEnum.PersonalInfo
      ? identityCriteriaToValues(initialCriteria)
      : IDENTITY_INITIAL_VALUES,
  );

  return (
    <Form<IdentitySearchFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={identitySearchFormValidationSchema}
      subscription={{ submitting: true, valid: true, dirty: true }}
      containerProps={{ mt: 0 }}
    >
      {({ submitting, valid, dirty }) => (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field
              name={IdentitySearchFormFields.ssn}
              component={MaskedTextField}
              label="SSN"
              mask={SSN_MASK}
              disabled={submitting || loading}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field
              name={IdentitySearchFormFields.last4Ssn}
              component={MaskedTextField}
              label="Last 4 of SSN"
              mask={LAST_4_SSN_MASK}
              disabled={submitting || loading}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field
              name={IdentitySearchFormFields.firstName}
              component={TextField}
              label="First name"
              disabled={submitting || loading}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field
              name={IdentitySearchFormFields.lastName}
              component={TextField}
              label="Last name"
              disabled={submitting || loading}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Field
              name={IdentitySearchFormFields.birthDate}
              component={DateField}
              label="Date of birth"
              disabled={submitting || loading}
              fullWidth
              disableFuture
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 6 }}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <Box display="flex" height="fit-content" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting || loading || !dirty}
                loading={submitting || loading}
              >
                Run search
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};

const IDENTITY_INITIAL_VALUES: Partial<IdentitySearchFormValues> = {
  [IdentitySearchFormFields.ssn]: null,
  [IdentitySearchFormFields.last4Ssn]: null,
  [IdentitySearchFormFields.firstName]: null,
  [IdentitySearchFormFields.lastName]: null,
  [IdentitySearchFormFields.birthDate]: null,
};
